import React from 'react';

import AddDatasetToAnalysisConfigurationModal, {
  AddDatasetToAnalysisConfiguration,
} from 'analysis-tool/AddDatasetToAnalysisConfigurationModal';
import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {ActionOrigin, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ActionType,
  CreateDatasetButton,
} from 'notebook/structured-tables/action-functions/createDatasetActionFunction';
import {
  AddDatasetToExistingAnalysisResponse,
  AddDatasetToNewAnalysisResponse,
} from 'notebook/structured-tables/ActionFunctionDAO';
import runModal from 'ui-library/runModal';

import {PrimaryActionRenderFn} from '../DataFrameActionToolbar';

async function addToAnalysis(datasetId: string, analysisId: string, dataFrameName: string): Promise<void> {
  await AnalysisActions.addOrReplaceAndRunAnalysisStep(analysisId, {
    stepType: PipelineStepType.DATA_CATALOG_DATASET,
    stepVersion: 0,
    stepConfig: {datasetId, dataFrameName},
  });
}

export interface AddDatasetToAnalysisButtonProps {
  datasetId: string;
  dataFrameName: string;
  dataFrameId: string;
  actionOrigin: ActionOrigin;
}

export function handleClickActionButton<TTableMetadata, TRowMetadata>(
  props: AddDatasetToAnalysisButtonProps,
  actionType: ActionType
): void {
  const {datasetId, dataFrameName: initialDatasetName, dataFrameId, actionOrigin} = props;

  async function handleAddDatasetToNewAnalysis(
    params: CreateAnalysisParams,
    dataFrameName: string
  ): Promise<AddDatasetToNewAnalysisResponse> {
    const {analysisId} = await AnalysisActions.createAnalysis({...params});

    return addToAnalysis(datasetId, analysisId, dataFrameName)
      .then((_) => {
        FileBrowserActions.openFile(analysisId);
        return {analysisId};
      })
      .catch((error) => {
        // Archive the analysis if addToAnalysis fails
        AnalysisActions.archiveAnalysis(
          analysisId,
          'Dataset could not be added to newly created Analysis',
          null // No result archive reason is needed, since the analysis is empty
        );
        throw error;
      });
  }

  function handleAddDatasetToExistingAnalysis(
    params: AddDatasetToAnalysisConfiguration
  ): Promise<AddDatasetToExistingAnalysisResponse> {
    return addToAnalysis(datasetId, params.analysisId, params.dataFrameName).then((_) => {
      FileBrowserActions.openFile(params.analysisId);
      return {dataFrameId};
    });
  }

  if (actionType === ActionType.NEW_ANALYSIS) {
    // Using a dynamic import() here because otherwise we get a large import cycle between notebook and pipeline code
    import('analysis-tool/create-analysis-modal/CreateAnalysisModal').then(({CreateAnalysisModal}) =>
      runModal(CreateAnalysisModal, {
        title: 'Add to new analysis',
        onCreateAnalysis: handleAddDatasetToNewAnalysis,
        actionOrigin,
        viaExistingTableProps: {initialDatasetName},
      })
    );
  } else {
    runModal(AddDatasetToAnalysisConfigurationModal, {
      onCreateDataFrame: handleAddDatasetToExistingAnalysis,
      initialDatasetName,
    });
  }
}

export function addDatasetToAnalysisButton(props: AddDatasetToAnalysisButtonProps): JSX.Element {
  return (
    <CreateDatasetButton
      disabled={false}
      key="AddToAnalysisButton"
      handleClickActionButton={(actionType) => {
        handleClickActionButton(props, actionType);
      }}
    />
  );
}

export function renderAddToAnalysisDropdown(props: AddDatasetToAnalysisButtonProps): PrimaryActionRenderFn {
  return () => addDatasetToAnalysisButton(props);
}
